import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { FEATURE_PERMISSIONS, hasPermission } from '../utils/permissions';
import { useAuth } from '../components/auth/AuthContext';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';

/**
 * Default task template for creating new tasks
 */
const DEFAULT_TASK = {
  taskTitle: '{opportunityLabel} Follow-up',
  taskDescription: '',
  actionItems: [],
  dueDays: 7,
  priority: 'Medium',
  assignTo: 'salesRep'
};

/**
 * Default opportunity stage workflows with multi-task support
 */
const DEFAULT_WORKFLOWS = {
  'New': {
    stage: 'New',
    isActive: true,
    tasks: [{
      taskTitle: '{opportunityLabel} Follow-up: Qualify Lead',
      taskDescription: 'Initial follow-up to qualify the new opportunity.',
      actionItems: [
        'Contact the buyer/decision maker',
        'Determine if Sofibella is the right fit and whether to move forward',
        'Send follow-up email with next steps or close out opportunity',
        'Move opportunity to correct stage before closing task'
      ],
      dueDays: 7,
      priority: 'Medium',
      assignTo: 'salesRep'
    }]
  },
  'Qualified': {
    stage: 'Qualified',
    isActive: true,
    tasks: [{
      taskTitle: '{opportunityLabel} Follow-up: Qualified Lead',
      taskDescription: 'Follow up with qualified customer to discuss needs.',
      actionItems: [
        'Contact the buyer/decision maker',
        'Discuss their needs, timeline, and budget',
        'Send follow-up email with next steps and credit application',
        'Change stage to Proposal Sent once approved, or Not Qualified if disqualified'
      ],
      dueDays: 7,
      priority: 'Medium',
      assignTo: 'salesRep'
    }]
  },
  'Not Qualified': {
    stage: 'Not Qualified',
    isActive: true,
    tasks: [{
      taskTitle: '{opportunityLabel} Follow-up: Not Qualified',
      taskDescription: 'Review disqualified opportunity and document reasons.',
      actionItems: [
        'Document the reason for disqualification',
        'Add notes to customer record for future reference',
        'Consider if this lead should be re-engaged in the future'
      ],
      dueDays: 7,
      priority: 'Low',
      assignTo: 'manager'
    }]
  },
  'Proposal Sent': {
    stage: 'Proposal Sent',
    isActive: true,
    tasks: [{
      taskTitle: '{opportunityLabel} Follow-up: Proposal Sent',
      taskDescription: 'Follow up with buyer on sent quote/proposal.',
      actionItems: [
        'Follow up with buyer regarding the quote',
        'Adjust or approve order quote in Apparel Magic',
        'Send order confirmation to buyer once approved',
        'Change stage to Closed Won'
      ],
      dueDays: 7,
      priority: 'High',
      assignTo: 'salesRep'
    }]
  },
  'Closed Won': {
    stage: 'Closed Won',
    isActive: true,
    tasks: [{
      taskTitle: '{opportunityLabel} Follow-up: Closed Won',
      taskDescription: 'Send welcome email to new customer.',
      actionItems: [
        'Send Welcome/Thank You email to customer',
        'Ensure customer is set up correctly in Apparel Magic',
        'Verify first order is processing correctly'
      ],
      dueDays: 7,
      priority: 'Medium',
      assignTo: 'manager'
    }]
  },
  'Closed Lost': {
    stage: 'Closed Lost',
    isActive: true,
    tasks: [{
      taskTitle: '{opportunityLabel} Follow-up: Closed Lost',
      taskDescription: 'Review closed opportunity and document lessons learned.',
      actionItems: [
        'Follow up with sales rep on closure reasons',
        'Identify lessons learned and any follow-up steps',
        'Add final notes to customer, contact(s), opportunity, or tasks',
        'Consider if this lead should be re-engaged in the future'
      ],
      dueDays: 7,
      priority: 'Low',
      assignTo: 'manager'
    }]
  }
};

/**
 * Normalize workflow data to multi-task format.
 * Handles legacy single-task docs (taskTitle at root level) by wrapping into tasks[].
 */
function normalizeWorkflow(data) {
  // Already has tasks array
  if (Array.isArray(data.tasks) && data.tasks.length > 0) {
    return { stage: data.stage, isActive: data.isActive !== false, tasks: data.tasks, id: data.id };
  }
  // Legacy single-task format: extract task fields into tasks[]
  if (data.taskTitle) {
    return {
      stage: data.stage,
      isActive: data.isActive !== false,
      id: data.id,
      tasks: [{
        taskTitle: data.taskTitle,
        taskDescription: data.taskDescription || '',
        actionItems: data.actionItems || [],
        dueDays: data.dueDays || 7,
        priority: data.priority || 'Medium',
        assignTo: data.assignTo || 'salesRep'
      }]
    };
  }
  // Fallback
  return { stage: data.stage, isActive: data.isActive !== false, tasks: [], id: data.id };
}

const STAGES = ['New', 'Qualified', 'Not Qualified', 'Proposal Sent', 'Closed Won', 'Closed Lost'];
const PRIORITIES = ['Low', 'Medium', 'High'];
const ASSIGNEE_TYPES = [
  { value: 'salesRep', label: 'Sales Rep (Opportunity Owner)' },
  { value: 'manager', label: 'Manager' }
];

const WorkflowEditorPage = () => {
  const { currentUser } = useAuth();
  const [workflows, setWorkflows] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedStage, setSelectedStage] = useState('New');
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [selectedTaskIndex, setSelectedTaskIndex] = useState(0);
  const [toast, setToast] = useState(null);
  const [newActionItem, setNewActionItem] = useState('');
  const [editingActionIndex, setEditingActionIndex] = useState(null);
  const [editingActionText, setEditingActionText] = useState('');

  // Permission for Workflow Automation
  const canManage = hasPermission(currentUser, FEATURE_PERMISSIONS.WORKFLOWS_MANAGE);
  const canView = canManage || hasPermission(currentUser, FEATURE_PERMISSIONS.WORKFLOWS_VIEW);
  const isReadOnly = !hasPermission(currentUser, FEATURE_PERMISSIONS.WORKFLOWS_MANAGE);

  // The currently selected task within the workflow
  const currentTask = editingWorkflow?.tasks?.[selectedTaskIndex] || null;

  // Load workflows from Firestore
  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(
      collection(db, collectionPath('workflowTemplates')),
      (snapshot) => {
        // Start with defaults (already in multi-task format)
        const merged = {};
        for (const key of Object.keys(DEFAULT_WORKFLOWS)) {
          merged[key] = { ...DEFAULT_WORKFLOWS[key], tasks: [...DEFAULT_WORKFLOWS[key].tasks] };
        }
        
        // Merge Firestore data, normalizing to multi-task format
        snapshot.docs.forEach(docSnap => {
          const docData = { ...docSnap.data(), id: docSnap.id };
          const stageName = docData.stage;
          if (stageName && merged[stageName]) {
            merged[stageName] = normalizeWorkflow(docData);
          }
        });
        
        setWorkflows(merged);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading workflows:', error);
        setToast({ message: `Error: ${error.message}`, type: 'error' });
        setWorkflows(DEFAULT_WORKFLOWS);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [canView]);

  // Set editing workflow when stage changes
  useEffect(() => {
    const source = workflows[selectedStage] || DEFAULT_WORKFLOWS[selectedStage];
    if (source) {
      setEditingWorkflow(JSON.parse(JSON.stringify(source)));
      setSelectedTaskIndex(0);
      setNewActionItem('');
      setEditingActionIndex(null);
      setEditingActionText('');
    }
  }, [selectedStage, workflows]);

  // --- Workflow-level handlers ---
  const handleWorkflowToggle = (value) => {
    if (isReadOnly) return;
    setEditingWorkflow(prev => ({ ...prev, isActive: value }));
  };

  // --- Task-level handlers (operate on tasks[selectedTaskIndex]) ---
  const updateCurrentTask = (field, value) => {
    if (isReadOnly) return;
    setEditingWorkflow(prev => {
      const tasks = [...prev.tasks];
      tasks[selectedTaskIndex] = { ...tasks[selectedTaskIndex], [field]: value };
      return { ...prev, tasks };
    });
  };

  const handleAddTask = () => {
    if (isReadOnly) return;
    setEditingWorkflow(prev => {
      const newTask = { ...DEFAULT_TASK, taskTitle: `{opportunityLabel} Follow-up: ${selectedStage} - Task ${prev.tasks.length + 1}` };
      const tasks = [...prev.tasks, newTask];
      return { ...prev, tasks };
    });
    // Switch to the newly added task
    setSelectedTaskIndex(editingWorkflow.tasks.length);
    setNewActionItem('');
    setEditingActionIndex(null);
  };

  const handleRemoveTask = (index) => {
    if (isReadOnly) return;
    if (editingWorkflow.tasks.length <= 1) {
      setToast({ message: 'Each stage must have at least one task.', type: 'error' });
      return;
    }
    if (!window.confirm(`Remove Task ${index + 1}? This cannot be undone.`)) return;
    setEditingWorkflow(prev => {
      const tasks = prev.tasks.filter((_, i) => i !== index);
      return { ...prev, tasks };
    });
    if (selectedTaskIndex >= editingWorkflow.tasks.length - 1) {
      setSelectedTaskIndex(Math.max(0, editingWorkflow.tasks.length - 2));
    }
    setNewActionItem('');
    setEditingActionIndex(null);
  };

  // --- Action item handlers (operate on current task's actionItems) ---
  const handleAddActionItem = () => {
    if (isReadOnly || !newActionItem.trim()) return;
    setEditingWorkflow(prev => {
      const tasks = [...prev.tasks];
      const task = { ...tasks[selectedTaskIndex] };
      task.actionItems = [...(task.actionItems || []), newActionItem.trim()];
      tasks[selectedTaskIndex] = task;
      return { ...prev, tasks };
    });
    setNewActionItem('');
  };

  const handleRemoveActionItem = (index) => {
    if (isReadOnly) return;
    setEditingWorkflow(prev => {
      const tasks = [...prev.tasks];
      const task = { ...tasks[selectedTaskIndex] };
      task.actionItems = task.actionItems.filter((_, i) => i !== index);
      tasks[selectedTaskIndex] = task;
      return { ...prev, tasks };
    });
  };

  const handleEditActionItem = (index) => {
    if (isReadOnly) return;
    setEditingActionIndex(index);
    setEditingActionText(currentTask?.actionItems?.[index] || '');
  };

  const handleSaveActionItem = () => {
    if (editingActionIndex === null || !editingActionText.trim()) return;
    setEditingWorkflow(prev => {
      const tasks = [...prev.tasks];
      const task = { ...tasks[selectedTaskIndex] };
      task.actionItems = task.actionItems.map((item, i) =>
        i === editingActionIndex ? editingActionText.trim() : item
      );
      tasks[selectedTaskIndex] = task;
      return { ...prev, tasks };
    });
    setEditingActionIndex(null);
    setEditingActionText('');
  };

  const handleCancelEditActionItem = () => {
    setEditingActionIndex(null);
    setEditingActionText('');
  };

  const handleMoveActionItem = (index, direction) => {
    const items = [...(currentTask?.actionItems || [])];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    updateCurrentTask('actionItems', items);
  };

  // --- Save / Reset ---
  const handleSave = async () => {
    if (isReadOnly || !editingWorkflow) return;
    setSaving(true);

    try {
      const docId = `stage_${selectedStage.toLowerCase().replace(/\s+/g, '_')}`;
      await setDoc(doc(db, collectionPath('workflowTemplates'), docId), {
        stage: selectedStage,
        isActive: editingWorkflow.isActive !== false,
        tasks: editingWorkflow.tasks,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid || 'unknown',
        updatedByName: currentUser?.displayName || 'Unknown User'
      });

      setToast({ message: 'Workflow saved successfully!', type: 'success' });
    } catch (error) {
      console.error('Error saving workflow:', error);
      setToast({ message: `Failed to save: ${error.message}`, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    if (window.confirm('Reset this workflow to defaults? This cannot be undone.')) {
      setEditingWorkflow(JSON.parse(JSON.stringify(DEFAULT_WORKFLOWS[selectedStage])));
      setSelectedTaskIndex(0);
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      'New': 'bg-teal-100 text-teal-800 border-teal-300',
      'Qualified': 'bg-blue-100 text-blue-800 border-blue-300',
      'Not Qualified': 'bg-yellow-100 text-yellow-600 border-yellow-300',
      'Proposal Sent': 'bg-purple-100 text-purple-800 border-purple-300',
      'Closed Won': 'bg-green-100 text-green-800 border-green-300',
      'Closed Lost': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  if (!canView) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <strong>Permission Denied:</strong> You do not have access to Workflow Automation.<br />
          Please contact your administrator if you believe this is an error.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <span className="ml-3 text-gray-600">Loading workflows...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header with improved Info Tooltip */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-black-900 dark:text-black-100 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" viewBox="0 0 24 24" fill="none">
            <circle cx="6" cy="6" r="3" fill="#C4B5FD" stroke="#7C3AED" strokeWidth="1.5"/>
            <circle cx="18" cy="6" r="3" fill="#BAE6FD" stroke="#0284C7" strokeWidth="1.5"/>
            <circle cx="12" cy="18" r="3" fill="#FED7AA" stroke="#EA580C" strokeWidth="1.5"/>
            <path d="M6 9v3h12V9" stroke="#6B7280" strokeWidth="1.5" fill="none"/>
            <path d="M12 12v3" stroke="#6B7280" strokeWidth="1.5"/>
          </svg>
          Workflow Automation
          <Tooltip
            text={
              <div className="max-w-xs">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-base text-gray-100 dark:text-gray-200">How Workflow Automation Works</span>
                </div>
                <ul className="mt-1 text-sm text-gray-100 dark:text-gray-200 space-y-2 list-disc list-inside font-normal">
                  <li>When an opportunity is created or its stage changes, one or more tasks are automatically created.</li>
                  <li>Each stage can have multiple tasks with different assignees and due dates.</li>
                  <li>Action items are added to each task as a checklist that users can check off.</li>
                  <li>Checking off action items prompts users to add notes, creating an audit trail.</li>
                  <li>Sales Rep tasks are assigned to the opportunity owner; Manager tasks go to admins/managers.</li>
                </ul>
              </div>
            }
            position="right"
            maxWidth={340}
          >
            <button
              type="button"
              className="ml-2 light:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none"
              aria-label="How Workflow Automation Works"
              style={{ lineHeight: 0 }}
            >
              <svg className="w-5 h-5 text-gray-500 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
            </button>
          </Tooltip>
        </h1>
        <p className="text-gray-600 dark:text-gray-600 mt-1">
          Configure automated task creation for opportunity stage transitions
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Stage Selector */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-4">
            <h3 className="text-sm font-semibold text-black-700 dark:text-black-300 mb-3 flex items-center gap-2">
                {/* Funnel Icon */}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" fill="#1dc6faff" stroke="#038cb6ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              Opportunity Stages
            </h3>
            <div className="space-y-2">
              {STAGES.map((stage) => (
                <button
                  key={stage}
                  onClick={() => setSelectedStage(stage)}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${
                    selectedStage === stage
                      ? `${getStageColor(stage)} ring-2 ring-offset-1 ring-indigo-500`
                      : 'bg-gray-50 light:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{stage}</span>
                    <div className="flex items-center gap-1.5">
                      {(workflows[stage]?.tasks?.length || 0) > 1 && (
                        <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium" title={`${workflows[stage].tasks.length} tasks`}>
                          {workflows[stage].tasks.length}
                        </span>
                      )}
                      {workflows[stage]?.isActive !== false && (
                        <span className="w-2 h-2 bg-green-500 rounded-full" title="Active"></span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Select a stage to edit its workflow configuration. Each stage can create multiple tasks automatically.
              </p>
            </div>
          </div>
        </div>

        {/* Workflow Editor */}
        <div className="lg:col-span-3">
          {editingWorkflow && (
            <div className="bg-white rounded-lg shadow-md">
              {/* Stage Header */}
              <div className={`px-6 py-4 rounded-t-lg ${getStageColor(selectedStage).replace('dark:bg-gray-800', 'bg-blue-50')}`}>
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <span>{selectedStage}</span>
                    <span className="text-sm font-normal opacity-75">Stage Workflow</span>
                    <span className="text-xs font-normal opacity-60">({editingWorkflow.tasks.length} task{editingWorkflow.tasks.length !== 1 ? 's' : ''})</span>
                  </h2>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingWorkflow.isActive !== false}
                      onChange={(e) => handleWorkflowToggle(e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm">Active</span>
                  </label>
                </div>
              </div>

              {/* Task Tabs */}
              <div className="border-b border-gray-200 bg-gray-50 px-6 flex items-center gap-1 overflow-x-auto">
                {editingWorkflow.tasks.map((task, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setSelectedTaskIndex(idx); setNewActionItem(''); setEditingActionIndex(null); }}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
                      selectedTaskIndex === idx
                        ? 'border-indigo-600 text-indigo-700 bg-white -mb-px rounded-t-lg'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Task {idx + 1}
                    {!isReadOnly && editingWorkflow.tasks.length > 1 && (
                      <Tooltip text="Remove task">
                        <span
                          onClick={(e) => { e.stopPropagation(); handleRemoveTask(idx); }}
                          className="ml-1 p-0.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </span>
                      </Tooltip>
                    )}
                  </button>
                ))}
                {!isReadOnly && (
                  <Tooltip text="Add another task to this stage">
                    <button
                      onClick={handleAddTask}
                      className="px-3 py-2.5 text-sm text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-t-lg border-b-2 border-transparent flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Task
                    </button>
                  </Tooltip>
                )}
              </div>

              {/* Task Editor Form */}
              {currentTask && (
              <div className="p-6 space-y-6 bg-white">
                {/* Task Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Task Title Template
                  </label>
                  <input
                    type="text"
                    value={currentTask.taskTitle || ''}
                    onChange={(e) => updateCurrentTask('taskTitle', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-900"
                    placeholder="e.g., {opportunityLabel} Follow-up: {stage}"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Use {'{opportunityLabel}'}, {'{customerName}'}, {'{stage}'} as variables
                  </p>
                </div>

                {/* Task Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Task Description
                  </label>
                  <textarea
                    value={currentTask.taskDescription || ''}
                    onChange={(e) => updateCurrentTask('taskDescription', e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-900"
                    placeholder="Description for the automated task..."
                  />
                </div>

                {/* Action Items */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Action Items (Checklist)
                  </label>
                  <div className="space-y-2 mb-3">
                    {(currentTask.actionItems || []).map((item, index) => (
                      <div key={index} className="flex items-center gap-2 bg-gray-100 rounded-lg p-2">
                        <span className="text-gray-400 text-sm w-6 text-center">{index + 1}.</span>
                        {editingActionIndex === index ? (
                          <div className="flex-1 flex items-center gap-2">
                            <input
                              type="text"
                              value={editingActionText}
                              onChange={(e) => setEditingActionText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveActionItem();
                                if (e.key === 'Escape') handleCancelEditActionItem();
                              }}
                              className="flex-1 border border-indigo-300 rounded px-2 py-1 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              autoFocus
                            />
                            <Tooltip text="Save (Enter)">
                              <button
                                onClick={handleSaveActionItem}
                                className="p-1 text-green-600 hover:text-green-800"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                            </Tooltip>
                            <Tooltip text="Cancel (Esc)">
                              <button
                                onClick={handleCancelEditActionItem}
                                className="p-1 text-gray-400 hover:text-gray-600"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </Tooltip>
                          </div>
                        ) : (
                          <>
                            <span className="flex-1 text-sm text-gray-700">{item}</span>
                            <div className="flex items-center gap-1">
                              {!isReadOnly && (
                                <Tooltip text="Edit">
                                  <button
                                    onClick={() => handleEditActionItem(index)}
                                    className="p-1 text-indigo-400 hover:text-indigo-600"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                </Tooltip>
                              )}
                              <Tooltip text="Move up">
                                <button
                                  onClick={() => handleMoveActionItem(index, -1)}
                                  disabled={index === 0}
                                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                </button>
                              </Tooltip>
                              <Tooltip text="Move down">
                                <button
                                  onClick={() => handleMoveActionItem(index, 1)}
                                  disabled={index === (currentTask.actionItems?.length || 0) - 1}
                                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              </Tooltip>
                              {!isReadOnly && (
                                <Tooltip text="Remove">
                                  <button
                                    onClick={() => handleRemoveActionItem(index)}
                                    className="p-1 text-red-400 hover:text-red-600"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                    {(!currentTask.actionItems || currentTask.actionItems.length === 0) && (
                      <p className="text-gray-400 text-sm text-center py-4">No action items defined</p>
                    )}
                  </div>
                  {!isReadOnly && (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newActionItem}
                        onChange={(e) => setNewActionItem(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddActionItem()}
                        placeholder="Add new action item..."
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900"
                      />
                      <button
                        onClick={handleAddActionItem}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
                      >
                        Add
                      </button>
                    </div>
                  )}
                </div>

                {/* Settings Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Due Days */}
                  <div>
                    <label className="block text-sm font-medium text-black-700 dark:text-black-300 mb-1">
                      Due Date (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="90"
                      value={currentTask.dueDays || 7}
                      onChange={(e) => updateCurrentTask('dueDays', parseInt(e.target.value) || 7)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-900"
                    />
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-black-700 dark:text-black-300 mb-1">
                      Priority
                    </label>
                    <select
                      value={currentTask.priority || 'Medium'}
                      onChange={(e) => updateCurrentTask('priority', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-900"
                    >
                      {PRIORITIES.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  {/* Assign To */}
                  <div>
                    <label className="block text-sm font-medium text-black-700 dark:text-black-300 mb-1">
                      Assign To
                    </label>
                    <select
                      value={currentTask.assignTo || 'salesRep'}
                      onChange={(e) => updateCurrentTask('assignTo', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-900"
                    >
                      {ASSIGNEE_TYPES.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  {!isReadOnly && (
                  <button
                    onClick={handleResetToDefault}
                    className="px-4 py-2 text-gray-700 bg-white border-2 border-gray-400 rounded-lg hover:border-indigo-500 hover:text-indigo-700 transition-colors text-sm font-medium"
                  >
                    Reset to Default
                  </button>
                  )}
                  {!isReadOnly ? (
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg font-medium flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save Changes
                      </>
                    )}
                  </button>
                  ) : (
                    <div className="text-sm text-gray-500 italic">View-only mode</div>
                  )}
                </div>
              </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default WorkflowEditorPage;
